import React from "react";
import { motion } from "framer-motion";
import { X, Pause, Play } from "lucide-react";
import EditableField from "./EditableField";
import RepeatPreview from "./RepeatPreview";
import TaskChecklist from "./TaskChecklist";
import TaskComments from "./TaskComments";
import TaskLiveTimer from "./TaskLiveTimer";
import { reminderLabelFromTask } from "../utils/taskReminderSync";
import { parseTimer } from "../utils/taskTimer";
import { isTaskPaused } from "../constants";

const TaskDetailDrawer = ({
    task,
    onClose,
    onUpdateField,
    onSnooze,
    snoozing = false,
    leadOptions,
    assignOptions,
    apiUrl,
    role,
    TaskHistory,
    onUpdateChecklist,
    onAddComment,
    onUploadChecklistPhoto,
    onStart,
    onPause,
    onSaveAsTemplate,
}) => {
    if (!task) return null;

    const readOnly = role === "staff" && task.assigned_by === "Admin";
    const hasReminder = String(task.reminder_type || "none").toLowerCase() !== "none";
    const isCompleted = String(task.status || "").toLowerCase() === "completed" || task.completed;

    const fields = [
        { label: "Title", field: "title" },
        { label: "Due Date", field: "due_date" },
        { label: "Due Time", field: "start_time" },
        { label: "Reminder", field: "reminder_preset" },
        { label: "Repetition", field: "repetition" },
        { label: "Priority", field: "priority" },
        { label: "Assign To", field: "assign_to" },
        { label: "Add to List", field: "list_id" },
        { label: "Notes", field: "notes", fullWidth: true },
        { label: "Attachment", field: "attachment", fullWidth: true },
    ];

    return (
        <>
            <motion.div
                initial={{ x: "100%", opacity: 0.9 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: "100%", opacity: 0.9 }}
                transition={{ type: "tween", duration: 0.28 }}
                className="task-detail-drawer"
            >
                <div className="task-detail-drawer__header">
                    <h2 className="text-base font-semibold text-ink truncate">Task Details</h2>
                    <button
                        type="button"
                        onClick={onClose}
                        className="task-detail-drawer__close"
                        aria-label="Close task details"
                    >
                        <X size={20} />
                    </button>
                </div>

                <div className="task-detail-drawer__body">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        {fields.map(({ label, field, fullWidth }) => (
                            <div
                                key={field}
                                className={fullWidth ? "col-span-1 md:col-span-2" : ""}
                            >
                                <p className="font-medium text-ink-muted text-xs mb-0.5">
                                    {label}
                                </p>
                                <EditableField
                                    value={
                                        field === "reminder_preset"
                                            ? reminderLabelFromTask(task)
                                            : task[field]
                                    }
                                    field={field}
                                    task={task}
                                    onChange={(val) => {
                                        if (field === "reminder_preset" && val && typeof val === "object") {
                                            onUpdateField(task.id, "reminder_type", val.reminder_type);
                                            onUpdateField(task.id, "reminder_minutes", val.reminder_minutes);
                                            return;
                                        }
                                        onUpdateField(task.id, field, val);
                                    }}
                                    leadOptions={leadOptions}
                                    assignOptions={assignOptions}
                                    assignedBy={task.assigned_by}
                                    apiUrl={apiUrl}
                                    readOnly={readOnly}
                                />
                            </div>
                        ))}
                    </div>
                    {!readOnly && hasReminder && !isCompleted && onSnooze && (
                        <div className="mt-4 rounded-xl border border-line bg-surface-2 p-3">
                            <p className="text-xs font-medium text-ink-muted mb-2">Snooze reminder</p>
                            <div className="flex flex-wrap gap-2">
                                <button
                                    type="button"
                                    disabled={snoozing}
                                    onClick={() => onSnooze(task.id, 10)}
                                    className="rounded-lg border border-line bg-surface px-3 py-1.5 text-xs font-medium text-ink-secondary hover:bg-surface-3 disabled:opacity-50"
                                >
                                    10 minutes
                                </button>
                                <button
                                    type="button"
                                    disabled={snoozing}
                                    onClick={() => onSnooze(task.id, 30)}
                                    className="rounded-lg border border-line bg-surface px-3 py-1.5 text-xs font-medium text-ink-secondary hover:bg-surface-3 disabled:opacity-50"
                                >
                                    30 minutes
                                </button>
                            </div>
                            {task.is_snoozed && task.snooze_until && (
                                <p className="text-[11px] text-primary mt-2">
                                    Snoozed until{" "}
                                    {new Date(task.snooze_until).toLocaleString("en-IN", {
                                        timeZone: "Asia/Kolkata",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                        day: "numeric",
                                        month: "short",
                                    })}
                                </p>
                            )}
                        </div>
                    )}
                    <RepeatPreview task={task} />
                    {!isCompleted && (
                        <div className="task-drawer-timer">
                            <TaskLiveTimer timer={task.timer} />
                            <div className="task-drawer-timer__actions">
                                {parseTimer(task.timer).running ? (
                                    <button
                                        type="button"
                                        onClick={() => onPause?.(task, "Timer pause")}
                                    >
                                        <Pause size={14} /> Pause
                                    </button>
                                ) : (
                                    <button type="button" onClick={() => onStart?.(task)}>
                                        <Play size={14} />
                                        {isTaskPaused(task) ? "Resume" : "Start"}
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                    <TaskChecklist
                        items={task.checklist}
                        canEdit={!readOnly}
                        canToggle={!isCompleted}
                        disabled={isCompleted}
                        actor={role === "staff" ? "staff" : "admin"}
                        apiUrl={apiUrl}
                        onChange={(next) => onUpdateChecklist?.(task.id, next)}
                        onUploadPhoto={(itemId, file) =>
                            onUploadChecklistPhoto?.(task.id, itemId, file)
                        }
                    />
                    {onSaveAsTemplate && (
                        <button
                            type="button"
                            className="task-drawer-template"
                            onClick={() => onSaveAsTemplate(task)}
                        >
                            Save as template
                        </button>
                    )}
                    <TaskComments
                        history={task?.historyTasks || []}
                        onSubmit={(text) => onAddComment?.(task.id, text)}
                        disabled={isCompleted}
                    />
                    {TaskHistory && (
                        <TaskHistory
                            history={(task?.historyTasks || []).filter(
                                (entry) => String(entry.title || "") !== "Comment"
                            )}
                        />
                    )}
                </div>
            </motion.div>
            <div
                className="task-detail-drawer__backdrop lg:hidden"
                onClick={onClose}
                aria-hidden
            />
        </>
    );
};

export default TaskDetailDrawer;
